import React, { useEffect } from 'react';
import clsx from 'clsx';
import Button from '../Button/Button';
import SmartImage from '../SmartImage/SmartImage';
import Notification from '../Notification/Notification';
import type { NotificationType, NotificationProps, NotificationTab } from '../Notification/Notification';

// Exporte os types para uso externo, se necessário
export type { NotificationType, NotificationTab };
export interface TopbarNotification extends NotificationType {}

// TopbarProps agora herda todas as props de Notification
interface TopbarProps extends NotificationProps {
  title: string;
  userName: string;
  onMenuClick: () => void;
  theme?: 'light' | 'dark';
  avatarUrl?: string;
  onThemeToggle?: () => void;
  userActions?: React.ReactNode;
  notificationComponent?: React.ReactNode;
  calendarButton?: React.ReactNode; // <-- adicione esta linha
  systemName?: string;
  companyName?: string;
  establishmentName?: string;
  clientLogoUrl?: string;
}

// Função utilitária para gerar cor baseada no nome
function stringToHslColor(str: string, s = 60, l = 50) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = hash % 360;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

// Função aprimorada para extrair iniciais ignorando preposições comuns
function getInitials(name: string) {
  const ignored = ['de', 'da', 'do', 'dos', 'das'];
  const parts = name
    .split(' ')
    .filter(word => word && !ignored.includes(word.toLowerCase()));
  return parts.map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

const Topbar: React.FC<TopbarProps> = ({
  title,
  userName,
  onMenuClick,
  theme = 'light',
  avatarUrl,
  onThemeToggle,
  userActions,
  notificationComponent,
  calendarButton, // <-- adicione aqui
  // Props de Notification (rest)
  notifications,
  onMarkAsRead,
  onMarkAsUnread,
  onMarkAsFavorite,
  onRemoveFavorite,
  onOpen,
  className,
  style,
  bellButtonAriaLabel,
  systemName,
  companyName,
  establishmentName,
  clientLogoUrl,
  // ...adicione outras props de Notification se necessário
}) => {
  // Sincroniza o título do browser com a prop title
  useEffect(() => {
    if (document.title !== title) {
      document.title = title;
    }
  }, [title]);

  return (
    <header
      className={clsx(
        'fixed top-0 left-0 right-0 z-30',
        'w-full flex flex-row items-center justify-between py-3',
        'backdrop-blur-md border-b',
        'min-h-[56px]',
        {
          // Glassmorphism Fluent UI
          'bg-white/60 border-gray-200 text-gray-900 shadow-[0_2px_8px_0_rgba(0,120,212,0.10)]': theme === 'light',
          'bg-[#201f1e]/60 border-[#292827] text-white shadow-[0_2px_8px_0_rgba(255,255,255,0.10)]': theme === 'dark',
        }
      )}
      style={{
        ...style,
        transition: 'background 0.2s, color 0.2s, box-shadow 0.2s',
        boxShadow:
          theme === 'dark'
            ? '0 2px 8px 0 rgba(255,255,255,0.10)'
            : '0 2px 8px 0 var(--accent-color, #2563eb)', // use a variável
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <div className="flex items-center md:w-auto md:justify-start pl-2 flex-shrink-0">
        {/* Logo do cliente, se houver */}
        {clientLogoUrl && (
          <SmartImage
            src={clientLogoUrl}
            alt={companyName || systemName || "Logo"}
            className="h-9 w-auto mr-3 rounded shadow-sm bg-white object-contain"
            style={{ maxWidth: 48 }}
            draggable={false}
            width={48}
            height={36}
            layout="fixed"
            ariaHidden={false}
          />
        )}
        <Button
          onClick={onMenuClick}
          ariaLabel="Abrir menu"
          theme={theme}
          variant="icon"
          size="tiny"
          buttonClassName={clsx(
            'rounded-full p-2 flex items-center justify-center border-0',
            'transition-all duration-200',
            'focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-400/60 focus-visible:ring-offset-2',
            theme === 'light'
              ? 'bg-white hover:bg-blue-50 active:bg-blue-100 text-blue-600 shadow'
              : 'bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-blue-200 shadow-lg'
          )}
          icon={
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="5" y1="7" x2="19" y2="7" />
              <line x1="5" y1="12" x2="19" y2="12" />
              <line x1="5" y1="17" x2="15" y2="17" />
            </svg>
          }
        />
      </div>
      {/* Título centralizado com subtítulo */}
      <div className="flex-1 min-w-0 flex flex-col items-center justify-center">
        <h1
          className={clsx(
            'text-xl font-semibold tracking-tight mx-3 text-center break-words',
            {
              'text-gray-900': theme === 'light',
              'text-white': theme === 'dark',
            }
          )}
          style={{ wordBreak: 'break-word' }}
        >
          {title}
        </h1>
        {/* Subtítulo com informações do sistema/empresa/estabelecimento */}
        {(systemName || companyName || establishmentName) && (
          <div className="text-xs font-normal text-gray-500 dark:text-gray-300 mt-[-2px]">
            {[systemName, companyName, establishmentName].filter(Boolean).join(" • ")}
          </div>
        )}
      </div>
      <div className="flex items-center justify-end w-auto flex-shrink-0">
        {/* Botão de alternância de tema */}
        {onThemeToggle && (
          <Button
            onClick={onThemeToggle}
            ariaLabel="Alternar tema"
            theme={theme}
            variant="icon"
            size="tiny"
            buttonClassName={clsx(
              'rounded-full p-2 mr-2 flex items-center justify-center border-0',
              theme === 'light'
                ? 'bg-white hover:bg-blue-50 active:bg-blue-100 text-blue-600 shadow'
                : 'bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-yellow-300 shadow-lg'
            )}
            icon={
              theme === 'dark' ? (
                // Ícone de sol para modo claro
                (<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="5" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>)
              ) : (
                // Ícone de lua para modo escuro
                (<svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z" />
                </svg>)
              )
            }
          />
        )}
        {/* Botão do calendário */}
        {calendarButton}
        {/* Notification genérico */}
        {notificationComponent
          ? <span className="ml-1">{notificationComponent}</span>
          : notifications && (
              <Notification
                notifications={notifications}
                onMarkAsRead={onMarkAsRead}
                onMarkAsUnread={onMarkAsUnread}
                onMarkAsFavorite={onMarkAsFavorite}
                onRemoveFavorite={onRemoveFavorite}
                onOpen={onOpen}
                theme={theme}
                className={className}
                style={style}
                bellButtonAriaLabel={bellButtonAriaLabel}
              />
            )
        }
        {/* Avatar estilo Fluent UI */}
        <span className="flex items-center gap-2">
          <span
            className={clsx('text-base font-medium truncate', {
              'text-gray-700': theme === 'light',
              'text-gray-200': theme === 'dark',
            })}
          >
            {userName}
          </span>
          {(avatarUrl || userName) && (
            <span
              className={clsx(
                "hidden md:inline-block h-6 w-px mx-2",
                theme === "light" ? "bg-gray-300" : "bg-slate-700"
              )}
              aria-hidden="true"
            />
          )}
          <div
            className={clsx(
              'w-9 h-9 rounded-full flex items-center justify-center font-semibold text-white uppercase select-none overflow-hidden',
              'transition-transform duration-300',
              'hover:scale-105 active:scale-95',
            )}
            style={{
              background: avatarUrl ? undefined : stringToHslColor(userName),
              boxShadow: theme === 'light'
                ? '0 2px 8px 0 rgba(59,130,246,0.15)'
                : '0 2px 12px 0 rgba(30,41,59,0.35)'
            }}
            tabIndex={0}
          >
            {avatarUrl ? (
              <SmartImage
                src={avatarUrl}
                alt={userName}
                className="w-full h-full object-cover rounded-full"
                width={36}
                height={36}
                layout="fixed"
                ariaHidden={false}
                errorFallback={getInitials(userName)}
                draggable={false}
                tooltip={userName}
              />
            ) : (
              getInitials(userName)
            )}
          </div>
        </span>
        {userActions}
      </div>
    </header>
  );
};

export default Topbar;